<template>
  <div
    :style="containerStyle"
    class="toggle-container row alignCentreRow"
    :class="[
      `toggle-${variant}`,
      { disabled: disabled, 'toggle-small': size === 'small' },
    ]"
  >
    <div class="row alignCentreRow">
      <label
        :for="controlId"
        class="nomargintopbottom"
        :class="{ 'active-label': isChecked }"
      >
        <slot name="icon-left"></slot>
        {{ label }}
      </label>
      <slot name="tooltip"></slot>
    </div>
    <span class="row alignCentreRow">
      <label class="switch">
        <input
          :id="controlId"
          type="checkbox"
          v-model="isChecked"
          :disabled="disabled"
          :aria-describedby="ariaDescribedby || undefined"
        />
        <span class="slider round" :style="sliderActiveStyle" v-haptic></span>
      </label>
      <!-- Conditionally render status text based on hideStatusText prop -->
      <div
        v-if="!hideStatusText"
        :class="['shortWidth', { purpleText: isChecked }]"
      >
        {{ isChecked ? "Yes" : "No" }}
      </div>
      <slot name="icon-right"></slot>
    </span>
  </div>
</template>

<script>
import { computed } from "vue";

let toggleSwitchInstanceCount = 0;

export default {
  name: "ToggleSwitch",
  props: {
    label: {
      type: String,
      required: true,
    },
    modelValue: {
      type: Boolean,
      required: true,
    },
    hideStatusText: {
      type: Boolean,
      default: false,
    },
    justifyToEnd: {
      type: Boolean,
      default: false,
    },
    justifyToStart: {
      type: Boolean,
      default: false,
    },
    justifyToStartEnd: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: true,
    },
    alignToCentre: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: "default",
      validator: (v) => ["default", "small"].includes(v),
    },
    variant: {
      type: String,
      default: "adaptive",
      validator: (v) => ["adaptive", "flat", "embossed"].includes(v),
    },
    activeColor: {
      type: String,
      default: null,
    },
    inputId: {
      type: String,
      default: "",
    },
    ariaDescribedby: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    toggleSwitchInstanceCount += 1;
    const controlId =
      props.inputId || `toggle-switch-${toggleSwitchInstanceCount}`;
    const isChecked = computed({
      get: () => props.modelValue,
      set: (newValue) => {
        if (newValue !== props.modelValue) emit("update:modelValue", newValue);
      },
    });

    const sliderActiveStyle = computed(() => {
      if (!isChecked.value || !props.activeColor) return {};
      return { "--toggle-active-color": props.activeColor };
    });

    const containerStyle = computed(() => {
      const styleObject = {};

      if (props.justifyToStart) {
        styleObject["justify-content"] = "flex-start";
      } else if (props.justifyToEnd) {
        styleObject["justify-content"] = "flex-end";
      } else if (props.alignToCentre) {
        styleObject["justify-content"] = "center";
      } else if (props.justifyToStartEnd) {
        styleObject["justify-content"] = "space-between";
      }

      if (props.fullWidth) {
        styleObject["width"] = "100%";
      }

      return styleObject;
    });

    return {
      isChecked,
      controlId,
      containerStyle,
      sliderActiveStyle,
    };
  },
};
</script>
<style scoped>
.shortWidth {
  width: 2.7ch;
  text-align: start;
}

.toggle-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.toggle-container.disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.switch {
  position: relative;
  display: inline-block;
  width: 54px;
  height: 30px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.switch input:focus-visible + .slider {
  outline: var(--focus-ring-width) solid var(--focus-ring-color);
  outline-offset: var(--focus-ring-offset);
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition:
    background-color var(--motion-standard) ease,
    box-shadow var(--motion-standard) ease;
  box-shadow: var(--shadow-segmented-track);
}

.toggle-container .active-label {
  color: var(--color-accent-emphasis);
}

.slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 5px;
  bottom: 5px;
  box-shadow: var(--shadow-surface-raised);
  background-color: var(--color-surface-raised);
  transition:
    transform var(--motion-standard) cubic-bezier(0.85, 0.05, 0.18, 1.35),
    background-color var(--motion-standard) ease,
    box-shadow var(--motion-standard) ease;
}

.toggle-flat .slider,
[data-theme="dark"] .toggle-adaptive .slider {
  border: 1px solid var(--color-border-control);
  background-color: var(--color-surface-muted);
  box-shadow: none;
}

.toggle-flat .slider:before,
[data-theme="dark"] .toggle-adaptive .slider:before {
  box-shadow: none;
}

/* active state handled by :before transform (retain hook for future if needed) */

input:checked + .slider:before {
  transform: translateX(24px);
  background-color: var(
    --toggle-active-color,
    var(--color-control-accent-muted)
  );
}

/* ── Small variant ──────────────────────────────────── */
.toggle-small .switch {
  width: 36px;
  height: 20px;
}

.toggle-small .slider:before {
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
}

.toggle-small input:checked + .slider:before {
  transform: translateX(16px);
}

/* Rounded sliders */
.slider.round {
  border-radius: var(--radius-pill);
}

.slider.round:before {
  border-radius: 50%;
}

@media (prefers-reduced-motion: reduce) {
  .slider,
  .slider:before {
    transition: none;
  }
}
</style>
